import { useState, useRef, useEffect, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';

interface SingleSelectDropdownProps {
  label: string;
  options: { value: string; label: string }[];
  value: string;
  onChange: (value: string) => void;
  className?: string;
  /**
   * 필터형(true, 기본값): value === '' 일 때 label을 placeholder로 표시, 값이 있으면 primary 강조.
   * 항상선택형(false): primary 강조 없음, label은 fallback placeholder 용도. 클리어 옵션 미노출.
   */
  clearable?: boolean;
}

export default function SingleSelectDropdown({
  label,
  options,
  value,
  onChange,
  className,
  clearable = true,
}: SingleSelectDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const selected = options.find((o) => o.value === value);
  const hasValue = value !== '';
  const isFilterActive = clearable && hasValue;

  return (
    <div className={`relative ${className ?? ''}`} ref={ref}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className={`flex items-center gap-1 px-3 py-1.5 text-sm border rounded-lg hover:bg-gray-50 transition-colors ${
          isFilterActive
            ? 'border-primary-500 bg-primary-50 text-primary-700'
            : 'border-gray-300 text-gray-700 bg-white'
        }`}
      >
        {selected?.label ?? label}
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 min-w-full w-max max-w-[320px] bg-white border border-gray-200 rounded-lg shadow-lg z-30 py-1 max-h-60 overflow-y-auto">
          {clearable && (
            <button
              type="button"
              onClick={() => {
                onChange('');
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 ${
                !hasValue ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
              }`}
            >
              {label}
            </button>
          )}
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              className={`w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 ${
                opt.value === value ? 'bg-primary-50 text-primary-700' : 'text-gray-700'
              }`}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
